import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { CommandLineIcon } from '@heroicons/react/24/outline';

const Loader = ({ finishLoading }) => {
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Initializing...');
  const [showLoader, setShowLoader] = useState(true);

  const loadingTexts = useMemo(() => [
    'Initializing...',
    'Loading dependencies...',
    'Fetching components...',
    'Compiling modules...',
    'Optimizing assets...',
    'Rendering UI...',
    'Starting application...',
    'Ready!'
  ], []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (progress < 100) {
        setProgress(prev => {
          // Much faster progress for better LCP
          const increment = 100 - prev < 10 ? 5 : Math.floor(Math.random() * 15) + 10;
          return Math.min(prev + increment, 100);
        });
      } else {
        // Faster completion for better LCP
        setTimeout(() => {
          setShowLoader(false);
          setTimeout(() => {
            finishLoading();
          }, 300); // Reduced from 1000ms
        }, 500); // Reduced from 2000ms
      }
    }, 50); // Reduced from 150ms for much faster progress

    return () => clearTimeout(timer);
  }, [progress, finishLoading]);

  useEffect(() => {
    // Update loading text based on progress
    const textIndex = Math.min(
      Math.floor((progress / 100) * loadingTexts.length),
      loadingTexts.length - 1
    );
    setLoadingText(loadingTexts[textIndex]);
  }, [progress, loadingTexts]);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900"
      initial={{ opacity: 1 }}
      animate={{ opacity: showLoader ? 1 : 0 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
      data-testid="loader-container"
    >
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-block mb-4"
          >
            <CommandLineIcon className="w-16 h-16 text-blue-500" />
          </motion.div>
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-3xl font-bold text-white font-mono mb-2"
          >
            Nidal Raed
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }} // Increased delay and duration
            className="text-blue-400 font-mono"
          >
            Portfolio Initialization
          </motion.p>
        </div>

        {/* Terminal-like loading interface */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }} // Increased delay and duration
          className="bg-gray-900 border border-blue-500/30 rounded-lg p-6 font-mono mb-8 shadow-lg shadow-blue-500/10"
          data-testid="terminal-interface"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-gray-400 text-sm ml-2">portfolio-loader.js</span>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="text-green-400">$ npm run start</div>
            <div className="text-gray-400"> portfolio@1.0.0 start</div>
            <div className="text-gray-400"> react-scripts start</div>
            
            <div className="text-blue-400 flex items-center">
              <span className="mr-2">$</span>
              <span className="text-yellow-400">{loadingText}</span>
              {progress < 100 && (
                <motion.span
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="ml-1 inline-block w-2 h-4 bg-blue-500"
                ></motion.span>
              )}
            </div>
            
            {progress === 100 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="text-green-400"
              >
                $ Compiled successfully!
              </motion.div>
            )}
            
            {progress === 100 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 1 }}
                className="text-yellow-400 font-bold"
              >
                $ Ready!
              </motion.div>
            )}
            
            {progress === 100 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 2 }}
                className="text-blue-400"
              >
                $ Opening portfolio...
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Progress bar */}
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: "100%", opacity: 1 }}
          transition={{ duration: 0.1 }} // Increased from 0.3s
          className="h-1 bg-gray-800 rounded-full mb-4 overflow-hidden"
          data-testid="progress-bar"
        >
          <motion.div
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full"
          ></motion.div>
        </motion.div>
        
        <div className="flex justify-between text-xs text-gray-500 font-mono">
          <span>{progress}%</span>
          <span>{progress === 100 ? "Complete" : "Loading..."}</span>
        </div>
      </div>
    </motion.div>
  );
};

export default Loader;
